// Damage system: every hit on an enemy or the player goes through here.
//
// hitEnemy: multipliers (stats, crit, abilities, vulnerable, shock-vs-electric, tag stats),
//   receiveDamage, statuses, batched knockback, events, run stats, damage numbers, hitstop,
//   kill handling (fragmenting / VOLATILE_PAYLOAD / GRAVITY_FURNACE detonations, WILDFIRE).
// hitPlayer: i-frames, incoming multipliers, shield → armour → hp, knockback, feedback, death.
// explode / chainLightning: shared area helpers used by weapons, items, abilities and enemies.

import { PLAYER_HIT_IFRAMES } from '../../config/constants';
import { COLORS } from '../../config/palette';
import type { DamageApi, EnemyApi, FxApi, GameContext, Vec } from '../../core/contracts';
import type { DamageInfo, DamageResult, StatusApplication, Tag } from '../../core/types';
import { chainOrder, type ChainNode } from './chain';
import { distToAabb } from './geom';
import {
  enemyList,
  flag,
  livePlayer,
  playerStats,
  withTag,
  type ChainOptsEx,
  type DamageInfoEx,
  type ExplodeOptsEx,
  type StatusSystem,
} from './internal';
import { capKnockback, explosionFalloff, incomingDamage, isCrit, outgoingMultiplier, splitPlayerDamage, VULNERABLE_BONUS } from './math';
import type { CombatSfx } from './sfx';
import { jitterPitch } from './sfx';
import { deathSnapshot } from './status';

/** Heavy-hit hitstop thresholds (ARCHITECTURE). */
const HEAVY_HIT = 60;
const HEAVY_HIT_STOP = 45;
const ELITE_KILL_STOP = 70;
/** VOLATILE_PAYLOAD: kill blasts. */
const VOLATILE_RADIUS = 90;
const VOLATILE_FRACTION = 0.25;
const VOLATILE_SELF_DAMAGE = 5;
/** GRAVITY_FURNACE: overheated enemies burst on death. */
const FURNACE_RADIUS = 120;
const FURNACE_FRACTION = 0.3;
/** Delay between chained kill detonations (reads as a chain reaction, and bounds recursion). */
const DETONATION_STAGGER = 0.07;
/** DoT damage numbers are summed per target and shown at this interval. */
const DOT_NUMBER_INTERVAL = 0.5;
const SHOCK_ON_CHAIN: StatusApplication = { id: 'shock', stacks: 1, duration: 2.5 };
const BURN_ON_CHAIN: StatusApplication = { id: 'burn', stacks: 1, duration: 3, potency: 5 };
const NO_RESULT: DamageResult = { dealt: 0, killed: false, blocked: true, absorbed: 0 };

interface Knock {
  x: number;
  y: number;
  max: number;
}

interface Detonation {
  delay: number;
  x: number;
  y: number;
  radius: number;
  damage: number;
  color: number;
  exclude: EnemyApi;
  hurtsPlayer: boolean;
  label: string;
  tags: Tag[];
}

interface DotNumber {
  amount: number;
  t: number;
  e: EnemyApi;
}

export function createDamage(ctx: GameContext, fx: FxApi, sfx: CombatSfx, status: StatusSystem): DamageApi {
  const knocks = new Map<EnemyApi, Knock>();
  const detonations: Detonation[] = [];
  const dotNumbers = new Map<EnemyApi, DotNumber>();
  const chainNodes: ChainNode[] = [];
  const chainEnemies: EnemyApi[] = [];
  let destroyed = false;

  const abilityOut = (target: EnemyApi | null, info: DamageInfo): number => {
    try {
      const v = ctx.abilities?.outgoingMultiplier(target, info);
      return typeof v === 'number' && Number.isFinite(v) ? v : 1;
    } catch {
      return 1;
    }
  };

  const abilityIn = (info: DamageInfo): number => {
    try {
      const v = ctx.abilities?.incomingMultiplier(info);
      return typeof v === 'number' && Number.isFinite(v) ? v : 1;
    } catch {
      return 1;
    }
  };

  const heldOverheated = (): boolean => {
    try {
      return !!ctx.player?.activeWeapon()?.overheated;
    } catch {
      return false;
    }
  };

  function multiplierFor(target: EnemyApi | null, info: DamageInfo): number {
    const st = target?.statuses;
    const vul = st?.get('vulnerable');
    return outgoingMultiplier({
      source: info.source,
      tags: info.tags ?? [],
      isDot: !!info.isDot,
      stats: playerStats(ctx),
      abilityMult: info.source === 'player' ? abilityOut(target, info) : 1,
      vulnerableBonus: vul ? (vul.potency > 0 ? vul.potency : VULNERABLE_BONUS) : 0,
      shocked: !!st?.has('shock'),
    });
  }

  function queueKnockback(target: EnemyApi, info: DamageInfo, kbStat: number): void {
    const base = info.knockback ?? 0;
    if (base <= 0 || info.isDot) return;
    const k = base * kbStat * (1 - Math.max(0, Math.min(1, target.knockbackResist)));
    if (k <= 0) return;
    const ang =
      info.kbAngle ??
      (info.sourceX !== undefined && info.sourceY !== undefined ? Math.atan2(target.y - info.sourceY, target.x - info.sourceX) : 0);
    let kx = Math.cos(ang) * k;
    // A small upward kick on ground targets sells the impact without launching them.
    let ky = Math.sin(ang) * k - (target.flying ? 0 : k * 0.12);
    const cur = knocks.get(target);
    if (cur) {
      kx += cur.x;
      ky += cur.y;
      cur.x = kx;
      cur.y = ky;
      cur.max = Math.max(cur.max, k);
    } else knocks.set(target, { x: kx, y: ky, max: k });
  }

  function showNumber(target: EnemyApi, amount: number, crit: boolean, dot: boolean): void {
    if (!ctx.settings?.damageNumbers || amount <= 0) return;
    if (dot) {
      const cur = dotNumbers.get(target);
      if (cur) cur.amount += amount;
      else dotNumbers.set(target, { amount, t: DOT_NUMBER_INTERVAL, e: target });
      return;
    }
    const jx = (Math.random() - 0.5) * target.radius * 0.8;
    fx.damageNumber(target.x + jx, target.y - target.radius * 0.7, amount, { crit });
  }

  function onKilled(target: EnemyApi, info: DamageInfoEx, snap: ReturnType<typeof deathSnapshot>): void {
    const run = ctx.run;
    const fromPlayer = info.source === 'player';
    if (target.kind !== 'prop' && fromPlayer) {
      run.stats.kills++;
      if (target.elite.length > 0) {
        run.stats.eliteKills++;
      }
    }
    if (target.kind !== 'prop' && target.elite.length > 0) ctx.time.hitstop(ELITE_KILL_STOP);

    // Fragmenting: the killing blow blows the target apart.
    if (info.explodeOnKill && info.explodeOnKill.radius > 0) {
      detonations.push({
        delay: 0,
        x: target.x,
        y: target.y,
        radius: info.explodeOnKill.radius * playerStats(ctx).explosionRadius,
        damage: Math.max(1, info.amount * info.explodeOnKill.damage),
        color: COLORS.explosive,
        exclude: target,
        hurtsPlayer: false,
        label: 'fragment',
        tags: ['explosive'],
      });
    }
    if (target.kind !== 'prop' && flag(ctx, 'VOLATILE_PAYLOAD')) {
      detonations.push({
        delay: DETONATION_STAGGER,
        x: target.x,
        y: target.y,
        radius: VOLATILE_RADIUS * playerStats(ctx).explosionRadius,
        damage: Math.max(4, target.maxHp * VOLATILE_FRACTION),
        color: COLORS.explosive,
        exclude: target,
        hurtsPlayer: true,
        label: 'volatile',
        tags: ['explosive'],
      });
    }
    if (snap.overheated && flag(ctx, 'GRAVITY_FURNACE')) {
      detonations.push({
        delay: DETONATION_STAGGER * 1.5,
        x: target.x,
        y: target.y,
        radius: FURNACE_RADIUS * playerStats(ctx).explosionRadius,
        damage: Math.max(8, target.maxHp * FURNACE_FRACTION),
        color: COLORS.burn,
        exclude: target,
        hurtsPlayer: false,
        label: 'furnace',
        tags: ['explosive', 'burn'],
      });
    }
    status.onKilled(target, snap);
    ctx.bus.emit('enemy:killed', { enemy: target, x: target.x, y: target.y, info });
  }

  const api: DamageApi = {
    hitEnemy(target, infoIn) {
      if (destroyed || !target || !target.alive || target.spawning) return NO_RESULT;
      const info = infoIn as DamageInfoEx;
      const fromPlayer = info.source === 'player';
      const stats = playerStats(ctx);
      let crit = false;
      if (fromPlayer && !info.isDot) {
        crit = info.crit ?? isCrit(ctx.rng.next(), stats.critChance);
      }
      const mult = multiplierFor(target, info) * (crit ? Math.max(1, stats.critMult) : 1);
      const final = Math.max(0, info.amount * mult);
      if (!(final > 0)) return { dealt: 0, killed: false, blocked: false, absorbed: 0 };

      const snap = deathSnapshot(target);
      const finalInfo: DamageInfoEx = { ...info, amount: final, crit };
      let res: DamageResult;
      try {
        res = target.receiveDamage(final, finalInfo);
      } catch (err) {
        console.warn('[combat] receiveDamage failed', err);
        return NO_RESULT;
      }
      if (res.blocked) return res;

      const total = res.dealt + res.absorbed;
      if (fromPlayer) {
        ctx.run.stats.damageDealt += total;
        if (total > ctx.run.stats.highestHit) ctx.run.stats.highestHit = Math.round(total);
      }

      if (!res.killed && info.statuses && info.statuses.length > 0) {
        for (const s of info.statuses) status.apply(target, s);
      }
      // Killed targets get the impulse too: enemies use it to fling their wreck.
      queueKnockback(target, info, fromPlayer ? stats.knockback : 1);

      showNumber(target, total, crit, !!info.isDot);
      if (!info.isDot) {
        if (res.absorbed > 0 && res.dealt <= 0) sfx.play('hit_shield', { volume: 0.5, x: target.x, pitch: jitterPitch() }, 45);
        else if (crit) sfx.play('hit_enemy_crit', { volume: 0.75, x: target.x, pitch: jitterPitch(0.05) }, 50);
        else sfx.play('hit_enemy', { volume: 0.5, x: target.x, pitch: jitterPitch() }, 38);
        if (final >= HEAVY_HIT) ctx.time.hitstop(HEAVY_HIT_STOP);
      }

      ctx.bus.emit('enemy:damaged', { enemy: target, amount: total, info: finalInfo, crit });
      if (res.killed) onKilled(target, finalInfo, snap);
      return res;
    },

    hitPlayer(info) {
      const p = livePlayer(ctx);
      if (destroyed || !p || !p.alive) return NO_RESULT;
      if (p.invulnerable && !info.ignoreIFrames) return NO_RESULT;
      const stats = p.stats;
      const amount = incomingDamage(info.amount, stats.damageTaken, abilityIn(info), heldOverheated());
      if (!(amount > 0)) return { dealt: 0, killed: false, blocked: false, absorbed: 0 };

      const shieldBefore = p.shield;
      const split = splitPlayerDamage(amount, shieldBefore, stats.armor);
      p.shield = split.shieldLeft;
      p.hp = Math.max(0, p.hp - split.hpDamage);
      const shieldBroken = shieldBefore > 0 && split.shieldLeft <= 0 && split.absorbed > 0;
      ctx.run.stats.damageTaken += split.absorbed + split.hpDamage;

      if (shieldBroken) {
        ctx.bus.emit('player:shieldBreak', {});
        sfx.play('shield_break', { volume: 0.9, x: p.x });
        fx.ring(p.x, p.y, 90, { color: COLORS.shield, duration: 320, width: 4, fromRadius: 30 });
        fx.sparks(p.x, p.y, 0, { count: 14, color: COLORS.shield, speed: 360, spread: Math.PI * 2, life: 360 });
      }
      try {
        p.setInvulnerable(PLAYER_HIT_IFRAMES * 1000);
        p.notifyDamaged(amount, info);
      } catch (err) {
        console.warn('[combat] player damage hooks failed', err);
      }

      const kb = (info.knockback ?? 0) * (1 - Math.max(0, Math.min(1, stats.knockbackResist)));
      if (kb > 0) {
        const ang =
          info.kbAngle ?? (info.sourceX !== undefined && info.sourceY !== undefined ? Math.atan2(p.y - info.sourceY, p.x - info.sourceX) : 0);
        try {
          p.applyKnockback(Math.cos(ang) * kb, Math.sin(ang) * kb - kb * 0.25);
        } catch {
          // player mid-teardown
        }
      }

      const hpHit = split.hpDamage > 0;
      if (info.source !== 'self' || hpHit) {
        fx.flash(COLORS.threat, hpHit ? 0.16 : 0.08, hpHit ? 140 : 90);
        fx.shake(hpHit ? 0.006 : 0.0035, hpHit ? 170 : 110);
        if (!shieldBroken) sfx.play(hpHit ? 'hit_player' : 'hit_shield', { volume: 0.85, x: p.x }, 60);
        if (hpHit) ctx.time.hitstop(30);
      }
      if (ctx.settings?.damageNumbers) fx.damageNumber(p.x, p.y - 56, split.absorbed + split.hpDamage, { player: true });

      ctx.bus.emit('player:damaged', {
        amount: split.absorbed + split.hpDamage,
        hp: p.hp,
        shield: p.shield,
        shieldBroken,
        info,
      });
      const killed = p.hp <= 0;
      if (killed) {
        p.hp = 0;
        try {
          p.kill();
        } catch (err) {
          console.warn('[combat] player.kill failed', err);
        }
      }
      return { dealt: split.hpDamage, killed, blocked: false, absorbed: split.absorbed };
    },

    explode(x, y, radius, info, optsIn) {
      const opts = (optsIn ?? {}) as ExplodeOptsEx;
      const hit: EnemyApi[] = [];
      if (destroyed || !(radius > 0)) return hit;
      const tags = info.tags ?? [];
      const showFx = opts.fx !== false;
      if (showFx) {
        const color = opts.color ?? (info.source === 'player' ? COLORS.explosive : COLORS.threatHot);
        fx.explosion(x, y, radius, color);
        sfx.play(radius >= 130 ? 'explosion_large' : 'explosion_small', { volume: radius >= 130 ? 0.95 : 0.75, x, pitch: jitterPitch(0.06) }, 45);
        const shake = opts.shake ?? Math.min(0.014, 0.0025 + radius * 0.00003);
        if (shake > 0) fx.shake(shake, radius >= 130 ? 260 : 170);
      }
      // Every explosion (any source) cracks and breaks nearby terrain.
      try {
        ctx.world.damageTerrain(x, y, info.amount, radius);
      } catch {
        // World without destructible terrain.
      }
      // Collect first: hitEnemy can kill and mutate the enemy list.
      const victims: EnemyApi[] = [];
      for (const e of enemyList(ctx)) {
        if (!e.alive || e.spawning || opts.exclude?.has(e)) continue;
        const d = Math.hypot(e.x - x, e.y - y) - e.radius;
        if (d <= radius) victims.push(e);
      }
      const kbBase = info.knockback ?? Math.min(700, radius * 4);
      for (const e of victims) {
        if (!e.alive) continue;
        const edge = Math.max(0, Math.hypot(e.x - x, e.y - y) - e.radius);
        const f = explosionFalloff(edge, radius);
        const r = api.hitEnemy(e, {
          ...info,
          tags,
          amount: info.amount * f,
          sourceX: x,
          sourceY: y,
          kbAngle: undefined,
          knockback: kbBase * (0.6 + 0.4 * f),
        });
        if (!r.blocked) hit.push(e);
      }
      if (opts.hurtsPlayer) {
        const p = livePlayer(ctx);
        if (p && p.alive && p.body) {
          const b = p.body;
          const d = distToAabb(x, y, b.x, b.y, b.x + b.width, b.y + b.height);
          if (d <= radius) {
            const f = explosionFalloff(d, radius);
            const own = info.source === 'player' || info.source === 'self';
            api.hitPlayer({
              amount: opts.playerDamage ?? (own ? VOLATILE_SELF_DAMAGE : info.amount * f),
              tags,
              source: own ? 'self' : info.source,
              sourceX: x,
              sourceY: y,
              knockback: own ? 220 : kbBase * 0.8,
              label: info.label ?? 'explosion',
            });
          }
        }
      }
      return hit;
    },

    chainLightning(from, first, optsIn) {
      const opts = optsIn as ChainOptsEx;
      const out: EnemyApi[] = [];
      if (destroyed) return out;
      // Candidates: targetable enemies (plus `first` even if it is a prop).
      chainNodes.length = 0;
      chainEnemies.length = 0;
      let firstIdx = -1;
      for (const e of enemyList(ctx)) {
        if (!e.alive || e.spawning) continue;
        if (opts.exclude?.has(e) && e !== first) continue;
        if (e.kind === 'prop' && e !== first) continue;
        if (e === first) firstIdx = chainEnemies.length;
        chainEnemies.push(e);
        chainNodes.push({ x: e.x, y: e.y, radius: e.radius });
      }
      if (first && firstIdx < 0) return out;
      const order = chainOrder(from, chainNodes, firstIdx, Math.max(0, Math.round(opts.count)), Math.max(0, opts.range));
      if (order.length === 0) return out;

      const tags: Tag[] = withTag(withTag(opts.tags, 'electric'), 'chain');
      const statuses: StatusApplication[] = [SHOCK_ON_CHAIN];
      if (flag(ctx, 'CHAIN_REACTOR')) statuses.push(BURN_ON_CHAIN);
      if (opts.statuses) for (const s of opts.statuses) if (s.id !== 'shock') statuses.push(s);
      const falloff = opts.falloff ?? 1;
      const color = opts.color ?? COLORS.electric;

      const pts: Vec[] = [{ x: from.x, y: from.y }];
      let px = from.x;
      let py = from.y;
      for (let i = 0; i < order.length; i++) {
        const e = chainEnemies[order[i]];
        pts.push({ x: e.x, y: e.y });
        if (!e.alive) continue;
        const r = api.hitEnemy(e, {
          amount: opts.damage * Math.pow(falloff, i),
          tags,
          source: 'player',
          sourceX: px,
          sourceY: py,
          knockback: 60,
          statuses,
          secondary: opts.secondary,
          noTrigger: opts.noTrigger,
          weaponId: opts.weaponId,
          label: opts.label ?? 'chain',
        });
        if (!r.blocked) out.push(e);
        px = e.x;
        py = e.y;
      }
      fx.lightning(pts, { color, width: opts.width ?? 3, duration: 130, jitter: 16, branches: order.length > 1 });
      for (let i = 1; i < pts.length; i++) fx.sparks(pts[i].x, pts[i].y, 0, { count: 3, color, speed: 200, spread: Math.PI * 2, life: 150 });
      sfx.play('chain_zap', { volume: 0.55, x: from.x, pitch: jitterPitch(0.1) }, 55);
      return out;
    },

    outgoingMultiplier(target, info) {
      return multiplierFor(target, info);
    },

    update(dt) {
      if (destroyed) return;
      // Batched knockback: several pellets / blasts on one target add up, capped at 1.5× the strongest.
      if (knocks.size > 0) {
        for (const [e, k] of knocks) {
          const v = capKnockback(k.x, k.y, k.max);
          try {
            e.knockback(v.x, v.y);
          } catch {
            // enemy mid-teardown
          }
        }
        knocks.clear();
      }
      // Kill detonations (staggered so chain reactions read and recursion stays bounded).
      if (detonations.length > 0) {
        const due: Detonation[] = [];
        for (let i = detonations.length - 1; i >= 0; i--) {
          const d = detonations[i];
          d.delay -= dt;
          if (d.delay <= 0) {
            due.push(d);
            detonations.splice(i, 1);
          }
        }
        for (const d of due) {
          api.explode(
            d.x,
            d.y,
            d.radius,
            { amount: d.damage, tags: d.tags, source: 'player', noTrigger: true, secondary: true, label: d.label, knockback: 380 },
            { color: d.color, exclude: new Set([d.exclude]), hurtsPlayer: d.hurtsPlayer, playerDamage: VOLATILE_SELF_DAMAGE } as ExplodeOptsEx,
          );
        }
      }
      if (dotNumbers.size > 0) {
        for (const [e, n] of dotNumbers) {
          n.t -= dt;
          if (n.t > 0) continue;
          if (n.amount >= 1 && e.alive) fx.damageNumber(e.x, e.y - e.radius * 0.9, n.amount, { color: COLORS.burn });
          dotNumbers.delete(e);
        }
      }
    },

    destroy() {
      destroyed = true;
      knocks.clear();
      detonations.length = 0;
      dotNumbers.clear();
    },
  };
  return api;
}
